import { loadModules } from 'esri-loader';
import React from 'react';
import ReactDOM from 'react-dom';
import BSLAExternalLinkComponent from '../../helpers/BSLAExternalLinkComponent';
import { DateTime } from 'luxon';
import BSLAPopupItemComponent from '../../helpers/BSLAPopupItemComponent';
import format from 'number-format.js';
import { sortInfos } from './BSLAConsEaseLayers';
import { SMALL_SCALE } from '../../BSLAConstants';

export const BSLAFDEPERPPermitsLayer = async () => {
    type layerModules = [typeof import('esri/layers/FeatureLayer')];
    const [FeatureLayer] = await (loadModules(['esri/layers/FeatureLayer']) as Promise<layerModules>);

    return new FeatureLayer({
        title: 'ERP Permits (FDEP)',
        url: 'https://ca.dep.state.fl.us/arcgis/rest/services/OpenData/ERP/MapServer/1',
        visible: false,
        minScale: SMALL_SCALE,
        popupTemplate: {
            outFields: ['*'],
            title: 'ERP Permits (FDEP)',
            content: (feature: __esri.Feature) => {
                const atts = feature.graphic.attributes;
                const node = document.createElement('div');

                const content = (
                    <React.Fragment>
                        <BSLAPopupItemComponent label="Status" value={atts.AGENCY_ACTION} />
                        <BSLAPopupItemComponent label="Project Name" value={atts.PROJECT_NAME} />
                        <BSLAPopupItemComponent label="Project ID" value={atts.PROJECT_ID} />
                        <BSLAPopupItemComponent label="Permit ID" value={atts.PERMIT_ID} />
                        <BSLAPopupItemComponent label="Applicant Company" value={atts.APPLICANT_COMPANY} />
                        <BSLAPopupItemComponent label="Location Name" value={atts.LOCATION_NAME} />
                        <BSLAPopupItemComponent label="Street Address" value={atts.STREET_ADDRESS} />
                        <BSLAPopupItemComponent label="Permitting Program" value={atts.PERMITTING_PROGRAM} />
                        <BSLAPopupItemComponent label="Permit Type" value={atts.PERMIT_TYPE} />
                        <BSLAPopupItemComponent
                            label="Agency Action Date"
                            value={atts.AGENCY_ACTION_DATE ? DateTime.fromMillis(atts.AGENCY_ACTION_DATE).toFormat('MMM dd, yyyy') : 'N/A'}
                        />
                        <div className="mt-3">
                            <BSLAExternalLinkComponent href={atts.DOCUMENTS} />
                        </div>
                    </React.Fragment>
                );

                ReactDOM.render(content, node);

                return node;
            }
        }
    });
};

export const BSLAWMDERPPermitsLayer = async () => {
    type layerModules = [
        typeof import('esri/layers/FeatureLayer'),
        typeof import('esri/layers/GroupLayer'),
        typeof import('esri/symbols/SimpleMarkerSymbol'),
        typeof import('esri/symbols/SimpleFillSymbol'),
        typeof import('esri/symbols/SimpleLineSymbol'),
        typeof import('esri/renderers/SimpleRenderer')
    ];
    const [FeatureLayer, GroupLayer, SimpleMarkerSymbol, SimpleFillSymbol, SimpleLineSymbol, SimpleRenderer] = await (loadModules([
        'esri/layers/FeatureLayer',
        'esri/layers/GroupLayer',
        'esri/symbols/SimpleMarkerSymbol',
        'esri/symbols/SimpleFillSymbol',
        'esri/symbols/SimpleLineSymbol',
        'esri/renderers/SimpleRenderer'
    ]) as Promise<layerModules>);

    const lineSymbol = new SimpleLineSymbol({
        color: [152, 230, 0, 1],
        width: 1
    });

    const fillSymbol = new SimpleFillSymbol({
        outline: lineSymbol,
        color: [152, 230, 0, 0.1]
    });

    const pointOutline = lineSymbol.clone();
    pointOutline.width = 2;

    const pointSymbol = new SimpleMarkerSymbol({
        color: [152, 230, 0, 0.1],
        outline: pointOutline,
        size: 9
    });

    const polygonRenderer = new SimpleRenderer({ symbol: fillSymbol });
    const pointRenderer = new SimpleRenderer({ symbol: pointSymbol });

    const infos = [
        {
            title: 'ERP Points (NWFWMD)',
            url: 'https://utility.arcgis.com/usrsvcs/servers/c207b72996684d7eb01ea70645ce5a65/rest/services/ERP_V2/FeatureServer/2',
            renderer: pointRenderer,
            popupContent: { content: nwfwmdPopupContent }
        },
        {
            title: 'ERP Boundary (SFWMD)',
            url: 'https://mapsprod.sfwmd.gov/map_ssl/rest/services/REG/FLPermitERP/MapServer/2',
            renderer: polygonRenderer,
            popupContent: { content: sfwmdPopupContent }
        },
        {
            title: 'ERP Boundary (SRWMD)',
            url: 'https://utility.arcgis.com/usrsvcs/servers/c207b72996684d7eb01ea70645ce5a65/rest/services/ERP_V2/FeatureServer/1',
            renderer: polygonRenderer,
            popupContent: { content: nwfwmdPopupContent }
        },
        {
            title: 'ERP Boundary (SJRWMD)',
            url: 'https://utility.arcgis.com/usrsvcs/servers/c207b72996684d7eb01ea70645ce5a65/rest/services/ERP_V2/FeatureServer/0',
            renderer: polygonRenderer,
            popupContent: { content: sjrwmdPopupContent }
        },
        {
            title: 'ERP Boundary (SWFWMD)',
            url: 'https://www25.swfwmd.state.fl.us/arcgis12/rest/services/OpenData/Regulatory/MapServer/10',
            renderer: polygonRenderer,
            popupContent: { content: swfwmdPopupContent }
        }
    ];

    const layers = infos.sort(sortInfos).map((i) => {
        return new FeatureLayer({
            minScale: SMALL_SCALE,
            url: i.url,
            title: i.title,
            renderer: i.renderer,
            visible: false,
            popupTemplate: {
                outFields: ['*'],
                title: i.title,
                ...i.popupContent
            }
        });
    });

    return new GroupLayer({
        layers,
        title: 'ERP Permits (Water Mgmt. Districts)'
    });
};

const nwfwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Applicant" value={atts.APPLICANT} />
            <BSLAPopupItemComponent label="Project Name" value={atts.SITE_NM} />
            <BSLAPopupItemComponent label="Site ID" value={atts.SITE_ID} />
            <BSLAPopupItemComponent label="Permit Status" value={atts.PRMT_STATUS} />
            <BSLAPopupItemComponent label="Permit Stage" value={atts.PRMT_STAGE} />
            <BSLAPopupItemComponent label="Date Applied" value={atts.RECv_DT ? DateTime.fromMillis(atts.RECv_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Permit Decision Date" value={atts.PRMT_DECISION_DT ? DateTime.fromMillis(atts.PRMT_DECISION_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Permit Expiration Date" value={atts.PRMT_EXPIR_DT ? DateTime.fromMillis(atts.PRMT_EXPIR_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <div className="mt-3">
                <BSLAExternalLinkComponent href={atts.LINK} />
            </div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const sfwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Project Name" value={atts.PROJECT_NAME} />
            <BSLAPopupItemComponent label="Permit Number" value={atts.PERMIT_NO} />
            <BSLAPopupItemComponent label="Permitee" value={atts.PERMITTEE} />
            <BSLAPopupItemComponent label="Issue Date" value={atts.ISSUE_DATE ? DateTime.fromMillis(atts.ISSUE_DATE).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Rule" value={atts.RULE_} />
            <div className="mt-3">
                <BSLAExternalLinkComponent href={atts.DOCUMENTS} />
            </div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const sjrwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Applicant" value={atts.APPLICANT} />
            <BSLAPopupItemComponent label="Project Name" value={atts.SITE_NM} />
            <BSLAPopupItemComponent label="Permit Stage" value={atts.PRMT_STAGE} />
            <BSLAPopupItemComponent label="Date Applied" value={atts.RECv_DT ? DateTime.fromMillis(atts.RECv_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Permit Decision Date" value={atts.PRMT_DECISION_DT ? DateTime.fromMillis(atts.PRMT_DECISION_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Permit Expiration Date" value={atts.PRMT_EXPIR_DT ? DateTime.fromMillis(atts.PRMT_EXPIR_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <div className="mt-3">
                <BSLAExternalLinkComponent href={atts.LINK} />
            </div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};

const swfwmdPopupContent = (feature: __esri.Feature) => {
    const atts = feature.graphic.attributes;
    const node = document.createElement('div');
    const content = (
        <React.Fragment>
            <BSLAPopupItemComponent label="Applicant" value={atts.PERMITTEE_NAME} />
            <BSLAPopupItemComponent label="Project Name" value={atts.PROJECT_NAME} />
            <BSLAPopupItemComponent label="Permit Department" value={atts.PERMIT_DEPARTMENT_NAME} />
            <BSLAPopupItemComponent label="Permit Status" value={atts.ERP_STATUS_DESC} />
            <BSLAPopupItemComponent label="Affected Acreage" value={format('###,###.00', atts.PROJECT_ACRES_MS)} />
            <BSLAPopupItemComponent label="Acres Owned" value={format('###,###.00', atts.OWNED_ACRES_MS)} />
            <BSLAPopupItemComponent
                label="Date Applied"
                value={atts.APPLICATION_RECEIVED_DT ? DateTime.fromMillis(atts.APPLICATION_RECEIVED_DT).toFormat('MMM dd, yyyy') : 'N/A'}
            />
            <BSLAPopupItemComponent label="Date Issued" value={atts.PERMIT_ISSUE_DT ? DateTime.fromMillis(atts.PERMIT_ISSUE_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Permit Expiration" value={atts.EXPIRATION_DT ? DateTime.fromMillis(atts.EXPIRATION_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent
                label="Construction Date"
                value={atts.CONSTRUCTION_NOTICE_DT ? DateTime.fromMillis(atts.CONSTRUCTION_NOTICE_DT).toFormat('MMM dd, yyyy') : 'N/A'}
            />
            <BSLAPopupItemComponent label="Inspection Date" value={atts.INSPECTION_DT ? DateTime.fromMillis(atts.INSPECTION_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Operation Date" value={atts.OPERATION_DT ? DateTime.fromMillis(atts.OPERATION_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <BSLAPopupItemComponent label="Last Update" value={atts.LAST_UPDATE_DT ? DateTime.fromMillis(atts.LAST_UPDATE_DT).toFormat('MMM dd, yyyy') : 'N/A'} />
            <div className="mt-3">
                <BSLAExternalLinkComponent href={atts.ERP_EXT_URL} />
            </div>
        </React.Fragment>
    );

    ReactDOM.render(content, node);

    return node;
};
